import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { xxLargeTextSize, semiBoldText, primaryText, baseTextSize, regularText, tertiaryText, smallTextSize, mediumText } from '../../styling/stylingHelper';
import { TimeControls, TimeViewOption } from './TimeControls';
import { TabbedSections, FilterOption } from './TabbedSections';
import { DropdownSelector } from './DropdownSelector';
import { cn } from '../../lib/utils';
import { TopSectionProps } from './TopSection';
import { ArrowLeft } from 'lucide-react';
import { filterOptionsTime, vatOptions } from './TopSection';
import { FilterButton } from './FilterButton';
import { useTranslation } from 'react-i18next';

interface TopSectionMobileProps extends TopSectionProps {
  overallTypeFilterOptions?: FilterOption[];
}

export const TopSectionMobile: React.FC<TopSectionMobileProps> = ({
  title,
  backButton,
  handleGoBack,
  dateRange,
  setDateRange,
  selectedTimeOption,
  setSelectedTimeOption,
  overallTypeFilterOptions,
  onOverallTypeChange,
  currentOverallTypeValue = 'all',
  onVatChange,
  currentVatValue,
  onRevenueVatChange,
  currentRevenueVatValue,
  onOrdersVatChange,
  currentOrdersVatValue,
  showFilters = false,
  filters,
  rightControls,
  showMobileTitle = false,
  collapseSecondaryControlsOnMobile = false,
}) => {
  const { t } = useTranslation();
  const [showSecondaryControls, setShowSecondaryControls] = useState(false);
  // Destructure orderFilters from filters object
  const orderFilters = filters?.orderFilters;
  const {
    restaurantFilters = [],
    setRestaurantFilters,
    availableRestaurants = [],
    platformFilters = [],
    setPlatformFilters,
    availablePlatforms = [],
    sectionFilters = [],
    setSectionFilters,
    availableSections = [],
    categoryFilters = [],
    setCategoryFilters,
    availableCategories = [],
    subCategoryFilters = [],
    setSubCategoryFilters,
    availableSubCategories = [],
    onResetFilters: orderResetFilters,
    activeFilterCount: orderActiveFilterCount = 0,
  } = orderFilters || {};

  // Destructure revenueFilters from filters object
  const revenueFilters = filters?.revenueFilters;
  const {
    restaurantFilters: revenueRestaurantFilters = [],
    setRestaurantFilters: setRevenueRestaurantFilters,
    availableRestaurants: availableRevenueRestaurants = [],
    revenueCategoryFilters = [],
    setRevenueCategoryFilters,
    availableRevenueCategories = [],
    revenueSourceFilters = [],
    setRevenueSourceFilters,
    availableRevenueSources = [],
    menuCategoryFilters = [],
    setMenuCategoryFilters,
    availableMenuCategories = [],
    onResetFilters: revenueResetFilters,
    activeFilterCount: revenueActiveFilterCount = 0,
  } = revenueFilters || {};

  // Destructure priceAlertFilters from filters object
  const priceAlertFilters = filters?.priceAlertFilters;
  const {
    restaurantFilters: priceAlertRestaurantFilters = [],
    setRestaurantFilters: setPriceAlertRestaurantFilters,
    availableRestaurants: availablePriceAlertRestaurants = [],
    platformFilters: priceAlertPlatformFilters = [],
    setPlatformFilters: setPriceAlertPlatformFilters,
    availablePlatforms: availablePriceAlertPlatforms = [],
    categoryFilters: priceAlertCategoryFilters = [],
    setCategoryFilters: setPriceAlertCategoryFilters,
    availableCategories: availablePriceAlertCategories = [],
    onResetFilters: priceAlertResetFilters,
    activeFilterCount: priceAlertActiveFilterCount = 0,
  } = priceAlertFilters || {};

  // Determine which filter type we're using
  const hasOrderFilters = !!(setRestaurantFilters || setPlatformFilters || setSectionFilters || setCategoryFilters);
  const hasRevenueFilters = !!(setRevenueRestaurantFilters || setRevenueCategoryFilters || setMenuCategoryFilters);
  const hasPriceAlertFilters = !!(setPriceAlertRestaurantFilters || setPriceAlertPlatformFilters || setPriceAlertCategoryFilters);

  // Use the appropriate reset function and count
  const onResetFilters = hasOrderFilters ? orderResetFilters : hasRevenueFilters ? revenueResetFilters : priceAlertResetFilters;
  const activeFilterCount = hasOrderFilters ? orderActiveFilterCount : hasRevenueFilters ? revenueActiveFilterCount : priceAlertActiveFilterCount;

  const hasOverallTypeFilter = overallTypeFilterOptions && overallTypeFilterOptions.length > 0;

  // Check if we're using separate VAT toggles (dashboard mode)
  const hasSeparateVatToggles = !!(onRevenueVatChange && onOrdersVatChange);

  // Dashboard focus mode: secondary controls (restaurant select, VAT toggles) hide
  // behind a muted "More options" toggle so the KPI cards sit right under the chips.
  const collapseSecondary = collapseSecondaryControlsOnMobile && (!!rightControls || hasSeparateVatToggles);

  // Create labeled VAT options for separate toggles
  const revenueVatOptions: FilterOption[] = useMemo(() => [
    { value: 'excl', label: `${t('dashboard.revenueVatLabel')} ${t('vatOptions.exclVat')}` },
    { value: 'incl', label: `${t('dashboard.revenueVatLabel')} ${t('vatOptions.inclVat')}` },
  ], [t]);

  const ordersVatOptions: FilterOption[] = useMemo(() => [
    { value: 'excl', label: `${t('dashboard.ordersVatLabel')} ${t('vatOptions.exclVat')}` },
    { value: 'incl', label: `${t('dashboard.ordersVatLabel')} ${t('vatOptions.inclVat')}` },
  ], [t]);

  return (
  <div className="space-y-4 mb-6">
    {/* Top: Title and paragraph aligned left.
        Below lg the page's MobilePageHeader already shows the title, so the <h1> is
        hidden there (unless showMobileTitle — pages without a MobilePageHeader).
        The back arrow always stays: MobilePageHeader has no back affordance.
        The subtitle is dashboard copy and is always hidden below lg. */}
    <div className="text-left">
      <div className='flex'>
        {backButton && (
          <ArrowLeft className="w-6 h-6 mt-1 mr-2" onClick={handleGoBack} />
        )}
        <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, !showMobileTitle && 'hidden lg:block')}>
          {title}
        </h1>
      </div>
      <p className={cn(baseTextSize, regularText, tertiaryText, "mt-1", 'hidden lg:block')}>
        {t('dashboard.salesSummaryDescription')}
      </p>
    </div>

    {/* Date navigation — full-width row (designer: ‹ label › under the header) */}
    <div className="w-full">
        <TimeControls
        startingDate={dateRange?.start || new Date()}
        selectedTimeOption={selectedTimeOption}
        dateRange={dateRange}
        onChange={(startDate: Date, endDate: Date) =>
            setDateRange({ start: startDate, end: endDate })
        }
        align="center"
        />
    </div>

    {rightControls && !collapseSecondary && (
      <div className="w-full flex justify-end">{rightControls}</div>
    )}

    {/* Compact chips row (designer): overall-type · time view · Filters/VAT.
        The Filters chip takes the designer's "Customize" slot. */}
    <div className="flex items-center w-full gap-2">
      {hasOverallTypeFilter && (
        <div className="flex-1 min-w-0">
          <TabbedSections
            options={overallTypeFilterOptions}
            onboardingId="overall-type"
            value={currentOverallTypeValue}
            isTimeOptionSelection={false}
            onChange={onOverallTypeChange}
            maxTabsBeforeDropdown={5}
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
          <TabbedSections
          options={filterOptionsTime}
          onboardingId="time-controls"
          value={selectedTimeOption}
          onChange={(value: string) => setSelectedTimeOption(value as TimeViewOption)}
          isTimeOptionSelection={true}
          />
      </div>
      {showFilters && onResetFilters && (hasOrderFilters || hasRevenueFilters || hasPriceAlertFilters) ? (
        <div className="flex-shrink-0">
            <FilterButton
              restaurantFilters={restaurantFilters}
              setRestaurantFilters={setRestaurantFilters}
              availableRestaurants={availableRestaurants}
              platformFilters={platformFilters}
              setPlatformFilters={setPlatformFilters}
              availablePlatforms={availablePlatforms}
              sectionFilters={sectionFilters}
              setSectionFilters={setSectionFilters}
              availableSections={availableSections}
              categoryFilters={categoryFilters}
              setCategoryFilters={setCategoryFilters}
              availableCategories={availableCategories}
              subCategoryFilters={subCategoryFilters}
              setSubCategoryFilters={setSubCategoryFilters}
              availableSubCategories={availableSubCategories}
              revenueCategoryFilters={revenueCategoryFilters}
              setRevenueCategoryFilters={setRevenueCategoryFilters}
              availableRevenueCategories={availableRevenueCategories}
              revenueSourceFilters={revenueSourceFilters}
              setRevenueSourceFilters={setRevenueSourceFilters}
              availableRevenueSources={availableRevenueSources}
              menuCategoryFilters={menuCategoryFilters}
              setMenuCategoryFilters={setMenuCategoryFilters}
              availableMenuCategories={availableMenuCategories}
              revenueRestaurantFilters={revenueRestaurantFilters}
              setRevenueRestaurantFilters={setRevenueRestaurantFilters}
              availableRevenueRestaurants={availableRevenueRestaurants}
              priceAlertRestaurantFilters={priceAlertRestaurantFilters}
              setPriceAlertRestaurantFilters={setPriceAlertRestaurantFilters}
              availablePriceAlertRestaurants={availablePriceAlertRestaurants}
              priceAlertPlatformFilters={priceAlertPlatformFilters}
              setPriceAlertPlatformFilters={setPriceAlertPlatformFilters}
              availablePriceAlertPlatforms={availablePriceAlertPlatforms}
              priceAlertCategoryFilters={priceAlertCategoryFilters}
              setPriceAlertCategoryFilters={setPriceAlertCategoryFilters}
              availablePriceAlertCategories={availablePriceAlertCategories}
              onVatChange={onVatChange}
              currentVatValue={currentVatValue}
              onResetFilters={onResetFilters}
              activeFilterCount={activeFilterCount}
              align="center"
              variant="chip"
            />
        </div>
      ) : !hasSeparateVatToggles && onVatChange ? (
        <div className="flex-1 min-w-0">
            <DropdownSelector
              options={vatOptions}
              defaultValue={currentVatValue || vatOptions[0].value}
              onChange={onVatChange}
            />
        </div>
      ) : null}
    </div>

    {/* Dashboard's two labeled VAT dropdowns keep their own row — four chips don't fit. */}
    {hasSeparateVatToggles && !collapseSecondary && (
      <div className="flex gap-2 max-[550px]:flex-col">
        <div className="flex-1">
          <DropdownSelector
            options={revenueVatOptions}
            defaultValue={currentRevenueVatValue || revenueVatOptions[0].value}
            onChange={onRevenueVatChange}
          />
        </div>
        <div className="flex-1">
          <DropdownSelector
            options={ordersVatOptions}
            defaultValue={currentOrdersVatValue || ordersVatOptions[0].value}
            onChange={onOrdersVatChange}
          />
        </div>
      </div>
    )}

    {/* Collapsed secondary controls: muted "More options" toggle above the KPIs. */}
    {collapseSecondary && (
      <div>
        <button
          type="button"
          onClick={() => setShowSecondaryControls((v) => !v)}
          aria-expanded={showSecondaryControls}
          className={cn('flex items-center gap-1', smallTextSize, mediumText, tertiaryText)}
        >
          {t('common.moreOptions')}
          <ChevronDown className={cn('w-4 h-4 transition-transform', showSecondaryControls && 'rotate-180')} />
        </button>
        {showSecondaryControls && (
          <div className="mt-2 flex flex-col gap-2">
            {rightControls && <div className="w-full">{rightControls}</div>}
            {hasSeparateVatToggles && (
              <>
                <DropdownSelector
                  options={revenueVatOptions}
                  defaultValue={currentRevenueVatValue || revenueVatOptions[0].value}
                  onChange={onRevenueVatChange}
                />
                <DropdownSelector
                  options={ordersVatOptions}
                  defaultValue={currentOrdersVatValue || ordersVatOptions[0].value}
                  onChange={onOrdersVatChange}
                />
              </>
            )}
          </div>
        )}
      </div>
    )}
  </div>
)};
